import type { ChartData, Point } from 'chart.js'

import { BarChartCard } from '@/components/charts/bar-chart-card'
import { DoughnutChartCard } from '@/components/charts/doughnut-chart-card'
import { TotalChart } from '@/modules/overview/ui/TotalChart'
import { formatNumberWithCommas } from '@/utils/helpers'

interface ChatterInspectCardsProps {
  totalSalesData:
    | {
        chartData: ChartData<'line', (number | Point | null)[], unknown>
        total: string
      }
    | undefined
  ppvPurchaseData:
    | {
        chartData: ChartData<
          'bar',
          (number | [number, number] | null)[],
          unknown
        >
        rate: string
      }
    | undefined
  sentMessagesData:
    | {
        chartData: ChartData<
          'bar',
          (number | [number, number] | null)[],
          unknown
        >
        total: number
      }
    | undefined
  keystrokesData:
    | {
        chartData: ChartData<
          'bar',
          (number | [number, number] | null)[],
          unknown
        >
        total: number
      }
    | undefined
  onlineTimeData:
    | {
        chartData: ChartData<'doughnut', number[], unknown>
        total: string
      }
    | undefined
  isFetching: boolean
  showCents?: boolean
}

export const ChatterInspectCards = ({
  totalSalesData,
  ppvPurchaseData,
  sentMessagesData,
  keystrokesData,
  onlineTimeData,
  isFetching,
  showCents,
}: ChatterInspectCardsProps) => {
  return (
    <div className="grid gap-6">
      <div className="grid grid-flow-row grid-cols-1 gap-4 xl:grid-cols-2 xl:gap-8">
        <TotalChart
          title="Total Sales"
          variant="big"
          data={totalSalesData?.chartData}
          totalValue={totalSalesData?.total || 0}
          isFetching={isFetching}
          isCurrency
          hasLegend
          showCents={showCents}
        />
        <BarChartCard
          title="PPV Purchase Rate"
          totalValue={`${ppvPurchaseData?.rate || 0}%`}
          data={ppvPurchaseData?.chartData}
          isFetching={isFetching}
          showLabels
        />
      </div>
      <div className="grid grid-flow-row grid-cols-1 gap-4 xl:grid-cols-2 xl:gap-8">
        <BarChartCard
          title="Sent Messages"
          totalValue={formatNumberWithCommas(sentMessagesData?.total)}
          data={sentMessagesData?.chartData}
          isFetching={isFetching}
          showLabels
          toggleOptions={{ fixedDecimalPoints: 0 }}
        />
        <BarChartCard
          title="Keystrokes"
          totalValue={formatNumberWithCommas(keystrokesData?.total)}
          data={keystrokesData?.chartData}
          isFetching={isFetching}
          showLabels
        />
      </div>
      <div className="xl:grid-colls-2 grid grid-flow-row grid-cols-1 gap-4 xl:gap-8">
        <DoughnutChartCard
          title="Online Time"
          data={onlineTimeData?.chartData}
          totalValue={onlineTimeData?.total || '0min'}
          isFetching={isFetching}
          toggleOptions={{ showLegendTitle: true, hideTooltipBody: true }}
        />
      </div>
    </div>
  )
}
